<template>
  <b-card id="cashflows" no-body v-if="Object.keys(collateralCashflow).length != 0">
    <b-card-header header-tag="header" class="p-0 mt-1">
      <b-btn block href="#" v-b-toggle.dealAnalysisResults variant="info" class="text-left">Available Analysis Results</b-btn>
    </b-card-header>
    <b-collapse id="dealAnalysisResults" visible>
      <b-tabs card>
        <b-tab title="Collateral cashflow" active>
          <b-btn v-if="Object.keys(collateralCashflow).length > 0"
            @click="exportCollateralCashflowToExcel()"
            variant="info"
            class="float-left mb-2">
            Export Cashflow to Excel
          </b-btn>
          <table class="table table-sm table-fixed">
            <caption class="table-caption text-left">
              {{getDealName()}}, Collateral Summary
            </caption>
            <thead class="text-right">
              <th>Original Balance</th>
              <th>Current Balance</th>
              <th>Deal Factor</th>
              <th>WAC</th>
              <th>WAM</th>
              <th>WALA</th>
              <th>Projected Losses</th>
            </thead>
            <tbody>
              <tr class="text-right">
                <td>{{collateralInfo.OriginalBalance}}</td>
                <td>{{collateralInfo.CurrentBalance}}</td>
                <td>{{collateralInfo.DealFactor}}</td>
                <td>{{collateralInfo.WAC}}</td>
                <td>{{collateralInfo.WAM}}</td>
                <td>{{collateralInfo.WALA}}</td>
                <td>{{collateralInfo.ProjectedLosses}}</td>
              </tr>
            </tbody>
          </table>
          <table class="table table-sm table-hover">
            <caption class="table-caption text-left">
              {{getDealName()}}, Collateral Cash Flow
              <b-form-checkbox id="show_debug_info_collateral" v-model="showDebugInfo" class="transformed">Show Trigger Info</b-form-checkbox>
            </caption>
            <thead>
            <tr>
              <th class="sticky">Period</th>
              <th class="text-right sticky">Payment<br/> Date</th>
              <th class="text-right sticky">Balance</th>
              <th class="text-right sticky">Scheduled<br/> Principal</th>
              <th class="text-right sticky">Prepaid<br/> Principal</th>
              <th class="text-right sticky">Defaults</th>
              <th class="text-right sticky">Losses</th>
              <th class="text-right sticky">Cumulative<br/> Losses</th>
              <!-- <th class="text-right sticky">Cumulative<br/> Defaults</th> -->
              <th class="text-right sticky">CPR<br/> Rate</th>
              <th class="text-right sticky">CDR<br/> Rate</th>
              <th class="text-right sticky">Loss Severity<br/> Rate</th>
              <th class="text-right sticky">Delinquency<br/> Rate</th>
              <template v-if="compDebugData">
                <template v-for="h of compDebugData.headers">
                  <th class="text-right sticky debug">{{h}}</th>
                </template>
              </template>
            </tr>
            </thead>
            <tbody>
              <template v-for="row in collateralCashflow">
                <tr :key=row.Period>
                  <td>{{row.Period}}</td>
                  <td class="text-right text-nowrap">{{row.PaymentDate}}</td>
                  <td class="text-right">{{row.Balance}}</td>
                  <td class="text-right">{{skipFirstRow(row.Period, row.ScheduledPrincipal)}}</td>
                  <td class="text-right">{{skipFirstRow(row.Period, row.PrepaidPrincipal)}}</td>
                  <td class="text-right">{{skipFirstRow(row.Period, row.Defaults)}}</td>
                  <td class="text-right">{{skipFirstRow(row.Period, row.Losses)}}</td>
                  <td class="text-right">{{row.CumulativeLosses}}</td>
                  <!-- <td class="text-right">{{skipFirstRow(row.Period, row.CumulativeDefaults)}}</td> -->
                  <td class="text-right">{{skipFirstRow(row.Period, row.CprRate)}}</td>
                  <td class="text-right">{{skipFirstRow(row.Period, row.CdrRate)}}</td>
                  <td class="text-right">{{skipFirstRow(row.Period, row.LossSeverityRate)}}</td>
                  <td class="text-right">{{skipFirstRow(row.Period, row.DelinquencyRate)}}</td>
                  <template v-if="compDebugData">
                    <template v-for="cell of compDebugData.rows[row.Period]">
                      <td class="text-right debug">{{cell}}</td>
                    </template>
                  </template>
                </tr>
              </template>
            </tbody>
          </table>
        </b-tab>
        <b-tab v-for="bond in bondsCashflows" :title=bond.BondName :key=bond.BondName>
          <b-btn v-if="Object.keys(collateralCashflow).length > 0"
            @click="exportBondCashflowToExcel(bond)"
            variant="info"
            class="float-left mb-2">
            Export Cashflow to Excel
          </b-btn>
          <table class="table table-sm table-fixed">
            <caption class="table-caption text-left">{{getDealName()}}, Bond {{bond.BondName}} Summary</caption>
            <thead class="text-right">
              <th>Original Balance</th>
              <th>Current Balance</th>
              <th>Factor</th>
              <th>Coupon</th>
              <th class="text-center">Floater Formula</th>
              <th class="text-right">Original Enhance</th>
              <th class="text-right">Original Thickness</th>
              <th class="text-right">Current Enhance</th>
              <th class="text-right">Current Thickness</th>
              <th>WAL</th>
              <th>Principal window</th>
              <th>Write Down</th>
            </thead>
            <tbody>
              <tr class="text-right">
                <td>{{bondsInfo[bond.BondName].OriginalBalance}}</td>
                <td>{{bondsInfo[bond.BondName].CurrentBalance}}</td>
                <td>{{bondsInfo[bond.BondName].Factor}}</td>
                <td>{{bondsInfo[bond.BondName].Coupon}}</td>
                <td class="text-center">{{bondsInfo[bond.BondName].FloaterFormula}}</td>
                <td class="text-right">{{bondsInfo[bond.BondName].OriginalEnhance}}</td>
                <td class="text-right">{{bondsInfo[bond.BondName].OriginalThickness}}</td>
                <td class="text-right">{{bondsInfo[bond.BondName].CurrentEnhance}}</td>
                <td class="text-right">{{bondsInfo[bond.BondName].CurrentThickness}}</td>
                <td>{{bondsInfo[bond.BondName].Wal}}</td>
                <td>{{bondsInfo[bond.BondName].PrincipalWindow}}</td>
                <td>{{bondsInfo[bond.BondName].WritedownPct}}</td>
              </tr>
            </tbody>
          </table>
          <bond-cashflow :bond="bond" />
        </b-tab>
      </b-tabs>
    </b-collapse>
  </b-card>
</template>

<script>
import BondCashflow          from './BondCashflow'
import FileSaver             from "file-saver";
import excelSupport          from "../../utilities/ExcelSupport";
import UtilsTableDataBuilder from "@/utilities/UtilsTableDataBuilder";

export default {
  props: {
    collateralCashflow: Array,
    bondsCashflows: Array,
    collateralInfo: Object,
    bondsInfo: Object,
    scenario: Object,
    runDate: Date,
    miscVariables: Array
  },
  data() {
    return {
      showDebugInfo: false
    }
  },
  computed: {
    compDebugData: function() {
      let res = null;
      if (this.showDebugInfo && this.miscVariables.length > 0) {
        const columnFormatters = [
          {DbName: "Pd", DataType: "String. Aligned left"},
          {DbName: "Period", DataType: "String. Aligned left"},
        ];
        res = new UtilsTableDataBuilder(this.miscVariables, columnFormatters);
      }
      return res;
    }
  },
  methods: {
    getDealName() {
      return this.$store.getters.dealName;
    },
    skipFirstRow(index, value) {
      return index != 0 ? value : ''
    },
    collateralCashflowToMatrix() {
      let cashflow = [ ['Period', 'Payment Date', 'Balance', 'Scheduled Principal', 'Prepaid Principal', 'Defaults', 'Losses',
        'Cumulative Losses', 'CPR Rate', 'CDR Rate', 'Loss Severity Rate', 'Delinquency Rate'] ]

      for (let row of this.collateralCashflow) {
        cashflow.push(
          [
            row.Period,
            row.PaymentDate,
            row.Balance,
            this.skipFirstRow(row.Period, row.ScheduledPrincipal),
            this.skipFirstRow(row.Period, row.PrepaidPrincipal),
            this.skipFirstRow(row.Period, row.Defaults),
            row.Losses,
            row.CumulativeLosses,
            this.skipFirstRow(row.Period, row.CprRate),
            this.skipFirstRow(row.Period, row.CdrRate),
            this.skipFirstRow(row.Period, row.LossSeverityRate),
            this.skipFirstRow(row.Period, row.DelinquencyRate)
          ])
      }
      let result = cashflow

      return result
    },
    bondCashflowToMatrix(bondCashflow) {
      let cashflow = [ ['Period', 'Payment Date', 'Balance', 'Interest', 'Principal', 'Rate', 'Cash',
        'Writedown', 'CPR Rate', 'CDR Rate', 'Loss Severity Rate', 'Delinquency Rate'] ]

      for (let row of bondCashflow) {
        cashflow.push(
          [
            row.Period,
            row.PaymentDate,
            row.Balance,
            this.skipFirstRow(row.Period, row.Interest),
            this.skipFirstRow(row.Period, row.Principal),
            this.skipFirstRow(row.Period, row.Rate),
            this.skipFirstRow(row.Period, row.Cash),
            row.ActualWriteDown,
            this.skipFirstRow(row.Period, row.CprRate),
            this.skipFirstRow(row.Period, row.CdrRate),
            this.skipFirstRow(row.Period, row.LossSeverityRate),
            this.skipFirstRow(row.Period, row.DelinquencyRate)
          ])
      }
      let result = cashflow

      return result
    },
    exportCollateralCashflowToExcel() {
      return excelSupport.exportCashflowToExcel(this.$store.getters.username, this.getDealName(), null, this.collateralCashflow[0].PaymentDate,
        this.runDate, this.scenario ? this.scenario.data : null, this.collateralCashflowToMatrix());
    },
    exportBondCashflowToExcel(bond) {
      return excelSupport.exportCashflowToExcel(this.$store.getters.username, this.getDealName(), bond.BondName, bond.BondCashflow[0].PaymentDate,
        this.runDate, this.scenario ? this.scenario.data : null, this.bondCashflowToMatrix(bond.BondCashflow));
    }
  },
  components: {
    BondCashflow
  }
}
</script>
